import React from 'react';
import { Card, Row, Col, Typography, Space, Button, Spin } from 'antd';
import { RobotOutlined, ThunderboltOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import Markdown from 'react-markdown';

import { AppDispatch, CombinedStates } from '../../core/types';
import { analyzeSurveyWithAI } from '../../core/survey/surveyApi';

const { Title, Paragraph } = Typography;

const AIDataAnalysis = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { singleSurvey, isFetching } = useSelector((state: CombinedStates) => state.survey);

    const handleStartAnalysis = () => {
        if (!singleSurvey?.id) return;
        dispatch(analyzeSurveyWithAI({ survey_id: singleSurvey.id }));
    };

    const isAnalyzed = singleSurvey?.ai_analyzed_at !== null && singleSurvey?.ai_analyzed_at !== undefined;
    const isPremade = singleSurvey?.is_premade === 1;

    return (
        <div>
            <Card>
                <Row gutter={[24, 24]}>
                    <Col span={24}>
                        <Space align="center" style={{ marginBottom: 24 }}>
                            <RobotOutlined style={{ fontSize: 24, color: '#1890ff' }} />
                            <Title level={2} style={{ margin: 0 }}>
                                Dirbtinio intelekto duomenų analizė
                            </Title>
                        </Space>
                        <Paragraph>
                            Šis skyrius pateikia automatinę apklausos duomenų analizę, atliekamą dirbtinio intelekto algoritmais.
                            Analizė apima respondentų elgsenos modelių identifikavimą, sentiment analizę ir rekomendacijas tolesniam gerinimui.
                        </Paragraph>
                    </Col>
                </Row>

                {!isPremade && (
                    <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
                        <Col span={24}>
                            {isAnalyzed ? (
                                // Show analysis results
                                <Card
                                    style={{
                                        background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                                        border: 'none',
                                        borderRadius: '12px',
                                        boxShadow: '0 8px 32px rgba(82, 196, 26, 0.3)',
                                    }}
                                    bodyStyle={{ padding: '32px' }}>
                                    <Row align="middle" justify="space-between">
                                        <Col xs={24} md={16}>
                                            <Space direction="vertical" size="middle">
                                                <Space align="center">
                                                    <CheckCircleOutlined style={{ fontSize: 24, color: 'white' }} />
                                                    <Title level={3} style={{ color: 'white', margin: 0 }}>
                                                        AI analizė baigta
                                                    </Title>
                                                </Space>
                                                <Paragraph style={{ color: 'rgba(255, 255, 255, 0.9)', margin: 0, fontSize: '16px' }}>
                                                    Analizė buvo atlikta {dayjs(singleSurvey?.ai_analyzed_at).format('YYYY-MM-DD HH:mm')}.
                                                    Žemiau pateikiami išsamūs analizės rezultatai ir rekomendacijos.
                                                </Paragraph>
                                            </Space>
                                        </Col>
                                        <Col xs={24} md={8} style={{ textAlign: 'right' }}>
                                            <Space direction="vertical" align="end">
                                                <Space>
                                                    <ClockCircleOutlined style={{ color: 'rgba(255, 255, 255, 0.8)' }} />
                                                    <span style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                                                        {dayjs(singleSurvey?.ai_analyzed_at).format('YYYY-MM-DD HH:mm')}
                                                    </span>
                                                </Space>
                                            </Space>
                                        </Col>
                                    </Row>
                                </Card>
                            ) : (
                                // Show start analysis button
                                <Card
                                    style={{
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        border: 'none',
                                        borderRadius: '12px',
                                        boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
                                    }}
                                    bodyStyle={{ padding: '32px' }}>
                                    <Row align="middle" justify="space-between">
                                        <Col xs={24} md={16}>
                                            <Space direction="vertical" size="middle">
                                                <Title level={3} style={{ color: 'white', margin: 0 }}>
                                                    Analizuoti duomenis su dirbtiniu intelektu
                                                </Title>
                                                <Paragraph style={{ color: 'rgba(255, 255, 255, 0.9)', margin: 0, fontSize: '16px' }}>
                                                    Po analizės dirbtinis intelektas pateiks apklausos santrauką,
                                                    identifikuos pagrindinius tendencijas ir pateiks konkrečias rekomendacijas
                                                    apklausos gerinimui ir rezultatų optimizavimui.
                                                </Paragraph>
                                            </Space>
                                        </Col>
                                        <Col xs={24} md={8} style={{ textAlign: 'right' }}>
                                            <Button
                                                type="primary"
                                                size="large"
                                                icon={isFetching ? <Spin size="small" /> : <ThunderboltOutlined />}
                                                loading={isFetching}
                                                disabled={isFetching}
                                                onClick={handleStartAnalysis}
                                                style={{
                                                    background: 'rgba(255, 255, 255, 0.2)',
                                                    border: '2px solid rgba(255, 255, 255, 0.3)',
                                                    borderRadius: '8px',
                                                    height: '48px',
                                                    padding: '0 24px',
                                                    fontSize: '16px',
                                                    fontWeight: '600',
                                                    color: 'white',
                                                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                                                    transition: 'all 0.3s ease',
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!isFetching) {
                                                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.15)';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!isFetching) {
                                                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                                                        e.currentTarget.style.transform = 'translateY(0)';
                                                        e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.1)';
                                                    }
                                                }}>
                                                {isFetching ? 'Analizuojama...' : 'Pradėti AI analizę'}
                                            </Button>
                                        </Col>
                                    </Row>
                                </Card>
                            )}
                        </Col>
                    </Row>
                )}

                {isPremade && (
                    <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
                        <Col span={24}>
                            <Card
                                style={{
                                    background: 'linear-gradient(135deg, #faad14 0%, #d48806 100%)',
                                    border: 'none',
                                    borderRadius: '12px',
                                    boxShadow: '0 8px 32px rgba(250, 173, 20, 0.3)',
                                }}
                                bodyStyle={{ padding: '32px' }}>
                                <Row align="middle" justify="center">
                                    <Col span={24} style={{ textAlign: 'center' }}>
                                        <Space direction="vertical" size="middle">
                                            <RobotOutlined style={{ fontSize: 48, color: 'white' }} />
                                            <Title level={3} style={{ color: 'white', margin: 0 }}>
                                                AI analizė negalima
                                            </Title>
                                            <Paragraph style={{ color: 'rgba(255, 255, 255, 0.9)', margin: 0, fontSize: '16px' }}>
                                                Šablono apklausos negali būti analizuojamos dirbtiniu intelektu.
                                                Sukurkite naują apklausą šios apklausos pagrindu, kad galėtumėte naudoti AI analizės funkcijas.
                                            </Paragraph>
                                        </Space>
                                    </Col>
                                </Row>
                            </Card>
                        </Col>
                    </Row>
                )}

                {isAnalyzed && singleSurvey?.ai_results && (
                    <Row gutter={[24, 24]}>
                        <Col span={24}>
                            <Card title="AI analizės rezultatai" size="small">
                                <div style={{
                                    lineHeight: '1.8',
                                    fontSize: '14px',
                                    color: '#333',
                                }}>
                                    <Markdown>
                                        {singleSurvey.ai_results}
                                    </Markdown>
                                </div>
                            </Card>
                        </Col>
                    </Row>
                )}
            </Card>
        </div>
    );
};

export default AIDataAnalysis;
